import type { WalkthroughAssetEntry } from './walkthroughTypes';

// CB:BEGIN homeowner-walkthrough-assets
export const homeownerWalkthroughAssets: WalkthroughAssetEntry[] = [
  {
    id: 'hw-getting-started',
    role: 'homeowner',
    sectionId: 'getting-started',
    title: 'Landing and sign in',
    description: 'Homeowner enters CoreBids and starts account flow.',
    rawImage: '/help/homeowner/s01_landing.png',
    annotatedImage: '/help/homeowner/s01_landing.png',
    thumbnailImage: '/help/homeowner/s01_landing.png',
    placeholderImage: '/help/placeholders/homeowner-generic.svg',
    alt: 'CoreBids homeowner landing and sign-in',
    annotationNotes: ['Highlight sign-in entry point', 'Show next-step guidance'],
    captureStatus: 'captured',
    sourceScreen: 'LandingPage',
    recommendedCrop: 'Header + primary CTA',
    priority: 'high',
  },
  {
    id: 'hw-creating-project',
    role: 'homeowner',
    sectionId: 'creating-a-project',
    title: 'Project wizard start',
    description: 'Project type and address start point.',
    rawImage: '/help/homeowner/s03_start_project.png',
    annotatedImage: '/help/homeowner/s03_start_project.png',
    thumbnailImage: '/help/homeowner/s03_start_project.png',
    placeholderImage: '/help/placeholders/homeowner-generic.svg',
    alt: 'Project wizard and new project start',
    captureStatus: 'captured',
    sourceScreen: 'ProjectWizard',
    recommendedCrop: 'Project type cards',
    priority: 'high',
  },
  {
    id: 'hw-submitting-details',
    role: 'homeowner',
    sectionId: 'submitting-project-details',
    title: 'Project details form',
    description: 'Required fields and intake form sections.',
    rawImage: '/help/homeowner/s04_details.png',
    annotatedImage: '/help/homeowner/s04_details.png',
    thumbnailImage: '/help/homeowner/s04_details.png',
    placeholderImage: '/help/placeholders/homeowner-generic.svg',
    alt: 'Homeowner project details and intake fields',
    captureStatus: 'captured',
    sourceScreen: 'EstimateForm',
    recommendedCrop: 'Required field block + continue CTA',
    priority: 'high',
  },
  {
    id: 'hw-after-submit',
    role: 'homeowner',
    sectionId: 'after-you-submit',
    title: 'Dashboard — bids waiting',
    description: 'Dashboard showing incoming bids and "Accept Bid" action.',
    rawImage: '/help/homeowner/04_dashboard_bids_pane.png',
    annotatedImage: '/help/homeowner/04_dashboard_bids_pane.png',
    thumbnailImage: '/help/homeowner/04_dashboard_bids_pane.png',
    placeholderImage: '/help/placeholders/homeowner-generic.svg',
    alt: 'Homeowner dashboard with incoming contractor bids',
    captureStatus: 'captured',
    sourceScreen: 'CustomerDashboard',
    recommendedCrop: 'Bids pane + Accept Bid CTA',
    priority: 'high',
  },
  {
    id: 'hw-viewing-bids',
    role: 'homeowner',
    sectionId: 'viewing-bids',
    title: 'Compare all bids',
    description: '3 contractor bid cards side-by-side with price and details.',
    rawImage: '/help/homeowner/05_bids_list.png',
    annotatedImage: '/help/homeowner/05_bids_list.png',
    thumbnailImage: '/help/homeowner/05_bids_list.png',
    placeholderImage: '/help/placeholders/homeowner-generic.svg',
    alt: 'Homeowner comparing contractor bids',
    captureStatus: 'captured',
    sourceScreen: 'ContractorBids',
    recommendedCrop: 'Bid cards with price comparison',
    priority: 'high',
  },
  {
    id: 'hw-selecting-contractor',
    role: 'homeowner',
    sectionId: 'selecting-a-contractor',
    title: 'Review full proposal',
    description: 'Full contractor proposal detail — scope, materials, warranty, price.',
    rawImage: '/help/homeowner/06_bid_proposal_detail.png',
    annotatedImage: '/help/homeowner/06_bid_proposal_detail.png',
    thumbnailImage: '/help/homeowner/06_bid_proposal_detail.png',
    placeholderImage: '/help/placeholders/homeowner-generic.svg',
    alt: 'Homeowner reviewing full contractor proposal',
    captureStatus: 'captured',
    sourceScreen: 'ContractorBids/BidDetail',
    recommendedCrop: 'Proposal detail + accept action',
    priority: 'high',
  },
  {
    id: 'hw-payments',
    role: 'homeowner',
    sectionId: 'payments-deposits',
    title: 'Pre-start checklist',
    description: '4-step checklist: sign contract, fund escrow, choose roof color, confirm start date.',
    rawImage: '/help/homeowner/08_project_start_tasks.png',
    annotatedImage: '/help/homeowner/08_project_start_tasks.png',
    thumbnailImage: '/help/homeowner/08_project_start_tasks.png',
    placeholderImage: '/help/placeholders/homeowner-generic.svg',
    alt: 'Homeowner pre-start checklist — 4 required steps',
    captureStatus: 'captured',
    captureMode: 'happy-path-demo',
    sourceScreen: 'P0ProjectFlow',
    recommendedCrop: 'All 4 checklist steps visible',
    priority: 'high',
  },
  {
    id: 'hw-milestones',
    role: 'homeowner',
    sectionId: 'project-progress-milestones',
    title: 'Track milestones',
    description: 'Milestone progress view with evidence review and approval actions.',
    rawImage: '/help/homeowner/10_milestones.png',
    annotatedImage: '/help/homeowner/10_milestones.png',
    thumbnailImage: '/help/homeowner/10_milestones.png',
    placeholderImage: '/help/placeholders/homeowner-generic.svg',
    alt: 'Homeowner milestone tracking and approvals',
    captureStatus: 'captured',
    sourceScreen: 'ProjectMilestonePage',
    recommendedCrop: 'Milestone cards + approve action',
    priority: 'high',
  },
  {
    id: 'hw-completion',
    role: 'homeowner',
    sectionId: 'completion',
    title: 'Project documents',
    description: 'Document panel showing signed contracts, warranties, and completion records.',
    rawImage: '/help/homeowner/11_documents.png',
    annotatedImage: '/help/homeowner/11_documents.png',
    thumbnailImage: '/help/homeowner/11_documents.png',
    placeholderImage: '/help/placeholders/homeowner-generic.svg',
    alt: 'Homeowner project documents and completion records',
    captureStatus: 'captured',
    sourceScreen: 'CustomerDashboard/Documents',
    recommendedCrop: 'Document list with download actions',
    priority: 'medium',
  },
  {
    id: 'hw-notifications',
    role: 'homeowner',
    sectionId: 'notifications-explained',
    title: 'Notifications and help',
    description: 'Help center with notification types and action guidance.',
    rawImage: '/help/homeowner/13_help_notifications.png',
    annotatedImage: '/help/homeowner/13_help_notifications.png',
    thumbnailImage: '/help/homeowner/13_help_notifications.png',
    placeholderImage: '/help/placeholders/homeowner-generic.svg',
    alt: 'Homeowner notifications and help center',
    captureStatus: 'captured',
    sourceScreen: 'HomeownerHelpPage',
    recommendedCrop: 'Notification types list',
    priority: 'medium',
  },
  {
    id: 'hw-common-questions',
    role: 'homeowner',
    sectionId: 'common-questions',
    title: 'Dashboard with bids',
    description: 'Dashboard showing bid status — context for common homeowner questions.',
    rawImage: '/help/homeowner/04_dashboard_bids_pane.png',
    annotatedImage: '/help/homeowner/04_dashboard_bids_pane.png',
    thumbnailImage: '/help/homeowner/04_dashboard_bids_pane.png',
    placeholderImage: '/help/placeholders/homeowner-generic.svg',
    alt: 'Homeowner dashboard for FAQ context',
    captureStatus: 'captured',
    sourceScreen: 'CustomerDashboard',
    priority: 'low',
  },
  {
    id: 'hw-support',
    role: 'homeowner',
    sectionId: 'contact-support',
    title: 'Notifications and help',
    description: 'Help center entry point with notification examples.',
    rawImage: '/help/homeowner/13_help_notifications.png',
    annotatedImage: '/help/homeowner/13_help_notifications.png',
    thumbnailImage: '/help/homeowner/13_help_notifications.png',
    placeholderImage: '/help/placeholders/homeowner-generic.svg',
    alt: 'Homeowner help center and support contact',
    captureStatus: 'captured',
    sourceScreen: 'HomeownerHelpPage',
    priority: 'medium',
  },
];
// CB:END homeowner-walkthrough-assets

